import axios, { AxiosInstance } from "axios";
import { HttpRequest } from "@aws-sdk/protocol-http";
import { SignatureV4 } from "@aws-sdk/signature-v4";
import { Sha256 } from "@aws-crypto/sha256-js";
import { AuthService } from "./auth";

class ApiError extends Error {
  public status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export class ApiClient {
  private httpClient: AxiosInstance;
  private apiBaseUrl: string;
  private signer: SignatureV4 | null = null;

  constructor(baseURL: string) {
    this.apiBaseUrl = baseURL;
    this.httpClient = axios.create({
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  setAuthService(authService: AuthService) {
    // Create signer when auth service is set
    this.signer = new SignatureV4({
      service: "execute-api",
      region: authService.getRegion(),
      credentials: async () => await authService.getAWSCredentials(),
      sha256: Sha256,
    });
  }

  async _sendRequest<T>(obj: HttpRequest): Promise<T> {
    if (!this.signer) {
      throw new Error("Auth service not initialized");
    }

    console.log(`Making ${obj.method} request to ${obj.path}`);

    const signedRequest = await this.signer.sign(obj);

    // Construct the full URL properly, including query parameters
    let fullPath = obj.path;
    if (obj.query && Object.keys(obj.query).length > 0) {
      const queryParams = new URLSearchParams();
      Object.entries(obj.query).forEach(([key, value]) => {
        if (typeof value === "string") {
          queryParams.append(key, value);
        } else if (Array.isArray(value)) {
          value.forEach((v) => queryParams.append(key, v));
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        fullPath = `${obj.path}?${queryString}`;
      }
    }
    const url = new URL(fullPath, this.apiBaseUrl);

    let response: any;
    try {
      response = await this.httpClient.request({
        method: signedRequest.method,
        url: url.toString(),
        headers: signedRequest.headers,
        data: signedRequest.body,
      });
    } catch (error: any) {
      console.error(
        `Request failed: ${error.response?.status} - ${error.response?.statusText}`,
      );
      throw error;
    }

    console.log(`Request completed: ${response.status}`);

    if (response.status < 400) {
      if (response.data && typeof response.data === "string") {
        return JSON.parse(response.data) as T;
      } else {
        return response.data as T;
      }
    } else {
      let message = response.statusText;
      if (response.data && typeof response.data === "string") {
        try {
          const json = JSON.parse(response.data);
          if (json.message) {
            message = json.message;
          }
        } catch (e) {
          message = response.data;
        }
      }
      throw new ApiError(response.status, message);
    }
  }

  async get<T>(path: string): Promise<T> {
    const url = new URL(path, this.apiBaseUrl);

    // For AWS SigV4, we need to properly handle query parameters
    const query: Record<string, string> = {};
    url.searchParams.forEach((value, key) => {
      query[key] = value;
    });

    const httpRequest = new HttpRequest({
      method: "GET",
      protocol: url.protocol,
      hostname: url.hostname,
      port: url.port ? parseInt(url.port) : undefined,
      path: url.pathname,
      query,
      headers: {
        host: url.host,
      },
    });

    return this._sendRequest<T>(httpRequest);
  }

  async post<T>(path: string, data: any): Promise<T> {
    const url = new URL(path, this.apiBaseUrl);
    const body = JSON.stringify(data);

    const httpRequest = new HttpRequest({
      method: "POST",
      protocol: url.protocol,
      hostname: url.hostname,
      port: url.port ? parseInt(url.port) : undefined,
      path: url.pathname + url.search,
      headers: {
        host: url.host,
        "content-type": "application/json",
        "content-length": body.length.toString(),
      },
      body,
    });

    return this._sendRequest<T>(httpRequest);
  }

  async put<T>(path: string, data: any): Promise<T> {
    const url = new URL(path, this.apiBaseUrl);
    const body = JSON.stringify(data);

    const httpRequest = new HttpRequest({
      method: "PUT",
      protocol: url.protocol,
      hostname: url.hostname,
      port: url.port ? parseInt(url.port) : undefined,
      path: url.pathname + url.search,
      headers: {
        host: url.host,
        "content-type": "application/json",
        "content-length": body.length.toString(),
      },
      body,
    });

    return this._sendRequest<T>(httpRequest);
  }

  async delete<T>(path: string): Promise<T> {
    const url = new URL(path, this.apiBaseUrl);

    const httpRequest = new HttpRequest({
      method: "DELETE",
      protocol: url.protocol,
      hostname: url.hostname,
      port: url.port ? parseInt(url.port) : undefined,
      path: url.pathname + url.search,
      headers: {
        host: url.host,
      },
    });

    return this._sendRequest<T>(httpRequest);
  }
}

// Create singleton instance
export const apiClient = new ApiClient(
  import.meta.env.VITE_API_ENDPOINT || window.location.origin,
);
